import {useCallback, useContext, useState} from 'react';
import {useAtomValue} from 'jotai';
import {useSendMessage} from './useSendMessage';
import {useScreenCreationContext} from './ScreenCreationContext';
import {
  isActiveSessionLimit,
  sessionLimitAtom,
} from '@features/subscription/sessionLimitAtom';
import {toast} from 'sonner';
import amplitudeService from '@features/analytics/amplitudeService';
import {captureException} from '@sentry/react';
import {
  createVariantPrompts,
  type CreateVariantAction,
} from '@features/ai/prompt/common/variants';
import {trpc} from '@features/utils/trpc';
import {FlowContext} from '@features/contexts/FlowContext';

export function useCreateVariant() {
  const {createScreenRelativeTo} = useScreenCreationContext();
  const {sendMessage} = useSendMessage();
  const [isPending, setIsPending] = useState(false);
  const trpcUtils = trpc.useUtils();
  const sessionLimit = useAtomValue(sessionLimitAtom);
  const {flow} = useContext(FlowContext);
  const isComponents = flow.genType === 'components';

  const createVariant = useCallback(
    async (screenId: string, prompt: string, action: CreateVariantAction) => {
      setIsPending(true);

      try {
        // Fetch the source screen's active structure to get screenshot URL
        const sourceStructure =
          await trpcUtils.screens.getActiveStructure.fetch({
            screenId,
          });
        const screenshotUrl = sourceStructure?.screenshotUrl ?? undefined;

        amplitudeService.logEvent('[Flow] create variant of screen', {
          screenId,
          prompt,
          action,
          hasScreenshot: !!screenshotUrl,
        });

        // For component flows, duplicate the source screen first (with its content).
        // For HTML/CSS flows, create a new empty screen.
        // Both are positioned to the right of the source.
        const variantLabel = createVariantPrompts[action].label;
        const targetScreenId = await createScreenRelativeTo(
          screenId,
          'right',
          isComponents
            ? {
                sourceScreenId: screenId,
                displayNameSuffix: ` (${variantLabel})`,
              }
            : undefined,
        );

        if (isActiveSessionLimit(sessionLimit)) {
          toast('Variation queued — generates when your session resets');
        } else {
          toast.success('Creating variation...');
        }

        // Send the variation message with createVariant attachment
        await sendMessage({
          screenId: targetScreenId,
          messageText: '',
          attachments: [
            {
              type: 'createVariant',
              variant: action,
              variantPrompt: prompt,
              sourceScreenId: screenId,
              screenshotUrl,
            },
          ],
        });
      } catch (error) {
        captureException(error);
        toast.error('Failed to create variation');
      } finally {
        setIsPending(false);
      }
    },
    [
      createScreenRelativeTo,
      sendMessage,
      trpcUtils,
      isComponents,
      sessionLimit,
    ],
  );

  return {createVariant, isPending};
}
